/** Tokens returned by Cognito (or mocked). */
export interface AuthTokens {
  accessToken: string
  idToken: string
  refreshToken: string
}

/** Minimal user representation derived from Cognito attributes. */
export interface AuthUser {
  email: string
  sub: string
  /** Stable internal id from the id-token `custom:edge_user_id` claim. A HARD
   * INVARIANT — always stamped (signup / migration / backfill); the session boundary
   * throws if it's absent. Equals `sub` until a real pool migration diverges it. */
  edgeUserId: string
  givenName: string
  familyName: string
  betaAccess: boolean
  /** Set (from the id-token `custom:migration_stage` claim) iff this user reached
   * the pool by migration. Drives the one-time `/provision-migrated-user` call —
   * new users and local/mock sessions leave it undefined. */
  migrationStage?: string
}

/** The id everything owner-scoped (IDB cache, analytics, Intercom) keys on. */
export function storageScopeId(user: Pick<AuthUser, 'edgeUserId'>): string {
  return user.edgeUserId
}

/** A validated auth session — tokens + user info. */
export interface AuthSession {
  tokens: AuthTokens
  user: AuthUser
}

/** Data required to register a new account. */
export interface RegisterData {
  email: string
  password: string
  givenName: string
  familyName: string
}

/** Data for completing a password reset. */
export interface PasswordResetData {
  email: string
  verificationCode: string
  newPassword: string
}

/**
 * Auth service interface — swappable between MockAuthService (dev)
 * and CognitoAuthService (prod). All methods return promises.
 */
export interface IAuthService {
  // Registration
  register(data: RegisterData): Promise<void>
  confirmRegistration(email: string, code: string): Promise<void>
  resendVerificationCode(email: string): Promise<void>

  // Login / Logout
  login(email: string, password: string): Promise<AuthSession>
  logout(): Promise<void>

  // Session management. These PRODUCE sessions; the account store holds them and is the
  // single source of truth for the current session and its tokens. The gateway keeps no
  // session state — read tokens via the request layer or services/auth/session-tokens.
  getSession(): Promise<AuthSession | null>
  refreshSession(): Promise<AuthSession | null>

  // Password reset
  sendPasswordResetCode(email: string): Promise<void>
  submitPasswordReset(data: PasswordResetData): Promise<void>

  // Profile updates
  updateAttributes(attributes: Record<string, string>): Promise<void>

  // Password change (authenticated)
  changePassword(oldPassword: string, newPassword: string): Promise<void>
}
